import { type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { ExamInfoBox } from './ExamInfoBox';
import { useMsLeftInTimeLimit } from '../hooks/useMsLeftInTimeLimit';
import { formatHoursMinutesSeconds } from '../formatHoursMinutesSeconds';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    Off-scale on purpose: the countdown is 50px to match the legacy box; text-3xl is too small.
*/

type Props = {
    timeRunsOutAt: number | null;
    timeLimitHours: number | null;

    // Whether the student has any progress in the stream, which decides between the "next lesson" and
    // "first lesson" wording.
    hasStarted: boolean;

    // Mirrors stream.inProgress, which the template used to turn the countdown coral.
    isInProgress: boolean;
    className?: string;
};

export const TimerBox: FC<Props> = ({ timeRunsOutAt, timeLimitHours, hasStarted, isInProgress, className }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });
    const msLeft = useMsLeftInTimeLimit({ timeRunsOutAt, timeLimitHours });

    if (!timeLimitHours) return null;

    const timerIsStarted = !!timeRunsOutAt;
    const i18nKey = timerIsStarted
        ? 'you_have_x_remaining'
        : hasStarted
          ? 'after_launching_next_you_will_have'
          : 'after_launching_first_you_will_have';

    return (
        <ExamInfoBox caption={t('exam_timer')} horizontalPadding="wide" centerBody className={className}>
            <p className="m-0 sm:text-md">
                <Trans
                    t={t}
                    i18nKey={i18nKey}
                    values={{ timeLeft: formatHoursMinutesSeconds(msLeft ?? 0) }}
                    components={{
                        timeLeft: (
                            <span
                                className={twMerge(
                                    'block text-[50px] font-semibold',
                                    isInProgress ? 'text-primary' : 'text-black',
                                )}
                            />
                        ),
                    }}
                />
            </p>
        </ExamInfoBox>
    );
};
